import axios from 'axios'
import Request, { apiBaseUrl } from './index'
import type { RequestConfig } from './types'
import type { ApiEnvelope } from '@etherworks/shared-types'

// ─── Shared-auth adapters ──────────────────────────────────────────────────
// Bypass the legacy `{code, data, msg}` interceptor by calling /api/auth/*
// directly with raw axios, then mapping responses to the legacy shape.

const authBase = `${apiBaseUrl}/auth`;

const rawAuth = axios.create({
	baseURL: authBase,
	withCredentials: true,
	timeout: 30000,
	validateStatus: () => true,
});

const svgToDataUrl = (svg: string): string => {
	const b64 = typeof window !== 'undefined' && (window as any).btoa
		? window.btoa(unescape(encodeURIComponent(svg)))
		: Buffer.from(svg, 'utf-8').toString('base64');
	return `data:image/svg+xml;base64,${b64}`;
};

const errMsg = (res: any, fallback: string): string =>
	res?.data?.error || res?.data?.message || fallback;

const userLanguage = (user: any): string | undefined => {
	const language = user?.preferences?.language;
	return typeof language === 'string' && language ? language : undefined;
};

// GET /auth/captcha → { code:0, data:{ captchaId, imageBase64 } }
export const generateCaptcha = async () => {
	const res = await rawAuth.get('/captcha');
	if (res.status === 200 && res.data?.id && res.data?.svg) {
		return {
			code: 0,
			data: { captchaId: res.data.id, imageBase64: svgToDataUrl(res.data.svg) },
		};
	}
	return { code: -1, msg: errMsg(res, 'captcha failed') };
};

// POST /auth/login
export const login = async (data: any) => {
	const body = data.challenge_token
		? {
			challengeToken: data.challenge_token,
			...(data.totp_code ? { twoFactorCode: data.totp_code } : {}),
		}
		: {
			email: data.email,
			password: data.password,
			captchaId: data.captcha_id,
			captchaAnswer: data.captcha_code,
			rememberMe: !!data.auto_login,
			...(data.totp_code ? { twoFactorCode: data.totp_code } : {}),
		};
	const res = await rawAuth.post('/login', body);
	if (res.status === 200 && res.data?.user) {
		return { code: 0, email: res.data.user.email, role: res.data.user.role || 'user', language: userLanguage(res.data.user) };
	}
	if (res.status === 200 && res.data?.requires2FA) {
		return { code: -16, challengeToken: res.data.challengeToken };
	}
	if (res.status === 401) {
		const err = String(res.data?.error || '');
		if (err.toLowerCase().includes('2fa')) return { code: -17, msg: err };
		return { code: -6, msg: err || 'Invalid credentials' };
	}
	if (res.status === 403 && res.data?.needsEmailVerification) {
		return { code: -5, msg: errMsg(res, 'Email not verified') };
	}
	if (res.status === 400) {
		const err = String(res.data?.error || '');
		if (err.toUpperCase().includes('CAPTCHA')) return { code: -20, msg: err };
		return { code: -1, msg: err };
	}
	return { code: -1, msg: errMsg(res, 'login failed') };
};

// POST /auth/logout
export const signout = async (): Promise<any> => {
	await rawAuth.post('/logout');
	return { code: 0 };
};

// "Send register code" → actually creates the account in shared-auth (which
// also sends the verification email). Caller must already have password+confirm
// filled in. UI calls this from the captcha modal.
export const sendRegisterCode = async (data: {
	email: string;
	captcha_id: string;
	captcha_code: string;
}) => {
	const body = {
		email: data.email,
		captchaId: data.captcha_id,
		captchaAnswer: data.captcha_code,
	};
	const res = await rawAuth.post('/register', body);
	if (res.status === 200 && res.data?.user) return { code: 0 };
	if (res.status === 409) {
		const resend = await rawAuth.post('/resend-verification', { email: data.email });
		if (resend.status === 200) return { code: 0 };
		return { code: -9, msg: errMsg(resend, 'email already registered') };
	}
	if (res.status === 400) {
		const err = String(res.data?.error || '');
		if (err.toUpperCase().includes('CAPTCHA')) return { code: -20, msg: err };
		return { code: -1, msg: err };
	}
	return { code: -50, msg: errMsg(res, 'send code failed') };
};

// "Register account" → in the shared-auth flow this just verifies the email
// code (the account was created in sendRegisterCode).
export const registerAccount = async (data: {
	email: string;
	email_code: string;
	password: string;
	confirm_password: string;
	referral_code?: string;
}) => {
	const res = await rawAuth.post('/verify-email', { email: data.email, code: data.email_code, password: data.password });
	if (res.status === 200) return { code: 0, role: 'user' };
	if (res.status === 400) return { code: -11, msg: errMsg(res, 'invalid code') };
	return { code: -1, msg: errMsg(res, 'verify failed') };
};

// "Reset password" → request the reset code email
export const resetPassword = async (data: {
	email: string;
	captcha_id: string;
	captcha_code: string;
}) => {
	const res = await rawAuth.post('/request-password-reset', {
		email: data.email,
		captchaId: data.captcha_id,
		captchaAnswer: data.captcha_code,
	});
	if (res.status === 200) return { code: 0, message: String(res.data?.message || '') };
	if (res.status === 400) {
		const err = String(res.data?.error || '');
		if (err.toUpperCase().includes('CAPTCHA')) return { code: -20, msg: err };
		return { code: -1, msg: err };
	}
	return { code: -1, msg: errMsg(res, 'request reset failed') };
};

// "Reset password by email" → submit code + new password
export const resetPasswordByEmail = async (data: {
	email: string;
	email_code: string;
	password: string;
	confirm_password: string;
}) => {
	const res = await rawAuth.post('/reset-password', {
		email: data.email,
		code: data.email_code,
		newPassword: data.password,
	});
	if (res.status === 200) return { code: 0, message: String(res.data?.message || '') };
	if (res.status === 400) return { code: -11, msg: errMsg(res, 'reset failed') };
	return { code: -1, msg: errMsg(res, 'reset failed') };
};

interface cloudRequestConfig<T> extends RequestConfig {
	data?: T
}
type CloudResponse<T> = Omit<ApiEnvelope<T>, 'data'> & (undefined extends T ? { data?: T } : { data: T }) & { message?: string }

const cloudRequest = <D, T = any>(config: cloudRequestConfig<D>) => {
	const { method = 'GET' } = config;
	if(method == 'get' || method == 'GET') {
		config.params = config?.data;
	}
	// Pass through timeout if specified
	return Request.request<CloudResponse<T>>(config);
}

const commonInterceptors = {
	requestInterceptors(res: any) {
		return res;
	},
	responseInterceptors(result: any) {
		return result;
	}
};

// Get current user info (including role) → adapter over /api/auth/me
export const getUserInfo = async (): Promise<{ code: number; email: string; role: string; language?: string; msg?: string }> => {
	const res = await rawAuth.get('/me');
	if (res.status === 200 && res.data?.user) {
		return { code: 0, email: res.data.user.email, role: res.data.user.role || 'user', language: userLanguage(res.data.user) };
	}
	return { code: -7, email: '', role: '', msg: errMsg(res, 'unauthorized') };
}

export const updateLanguagePreference = async (language: string): Promise<{ code: number; msg?: string }> => {
	const res = await rawAuth.patch('/preferences', { language });
	if (res.status === 200) return { code: 0 };
	return { code: -1, msg: errMsg(res, 'update language failed') };
}
// =====================================================
// PayPal Payment APIs
// =====================================================

// Get PayPal client ID for SDK initialization
export const getPayPalClientId = () => {
	interface Res {
		client_id: string
		mode: 'sandbox' | 'live'
	}
	return cloudRequest<{}, Res>({
		url: '/payments/paypal/client-id',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Create PayPal order (creates local order + PayPal order in one step)
export const createPayPalOrder = (amount: number) => {
	interface Res {
		paypal_order_id: string
		order_id: string
		amount: number
	}
	return cloudRequest<{ amount: number }, Res>({
		url: '/payments/paypal/create-order',
		method: 'POST',
		data: { amount },
		interceptors: commonInterceptors
	})
}

// Capture PayPal order after user approval
export const capturePayPalOrder = (orderId: string, paypalOrderId: string) => {
	interface Res {
		success: boolean
		order_id: string
		paypal_order_id: string
		amount: number
		new_balance: number
	}
	return cloudRequest<{ order_id: string; paypal_order_id: string }, Res>({
		url: '/payments/paypal/capture-order',
		method: 'POST',
		data: { order_id: orderId, paypal_order_id: paypalOrderId },
		interceptors: commonInterceptors
	})
}

// Process credit card payment
export const processCreditCardPayment = (data) => {
	interface Req {
		order_id: number
		card_number?: string
		expiry?: string
		cvc?: string
		cardholder_name?: string
		payment_method_id?: string
		save_card?: boolean
		billing_details?: any
	}
	return cloudRequest<Req, void>({
		url: '/payments/creditcard',
		method: 'POST',
		data,
		interceptors: commonInterceptors
	})
}

// Process credit card payment with backend delay (simulates decline)
// Creates order with payment_method: 'credit_card' and returns decline after 5-8s
export const processCreditCardPaymentWithDelay = (data: {
	payment_method_id?: string
	amount: number
}) => {
	return cloudRequest<typeof data, void>({
		url: '/payments/creditcard/process',
		method: 'POST',
		data,
		interceptors: commonInterceptors
	})
}

// Get saved payment methods
export const getPaymentMethods = () => {
	interface Res {
		id: string
		type: string
		is_default: boolean
		card: {
			holder_name: string
			last4: string
			brand: string
			expiry_month: string
			expiry_year: string
		}
		billing_details: any
		created_at: number
	}
	return cloudRequest<{}, Res[]>({
		url: '/payments/methods',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Add new payment method
export const addPaymentMethod = (data: {
	card: {
		number: string
		holder_name: string
		expiry_month: string
		expiry_year: string
		cvc: string
	}
	billing_details: {
		address_line1: string
		address_line2?: string
		city?: string
		state?: string
		postal_code?: string
		country: string
	}
}) => {
	return cloudRequest<typeof data, void>({
		url: '/payments/methods',
		method: 'POST',
		data,
		interceptors: commonInterceptors
	})
}

// Delete payment method
export const deletePaymentMethod = (id: string) => {
	return cloudRequest<{}, void>({
		url: `/payments/methods/${id}`,
		method: 'DELETE',
		interceptors: commonInterceptors
	})
}

// Set default payment method
export const setDefaultPaymentMethod = (id: string) => {
	return cloudRequest<{}, void>({
		url: `/payments/methods/${id}/default`,
		method: 'PUT',
		interceptors: commonInterceptors
	})
}
// User center dashboard
export const userDashboard = () => {
	return cloudRequest({
		url: '/user/dashboard',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Gift Card APIs
export interface GiftCard {
	id: string
	card_number_masked: string
	card_number: string
	password: string | null  // Password for unredeemed cards, null if redeemed
	amount: number
	created_at: number
	is_redeemed: boolean
	redeemed_by: string | null
	redeemed_at: number | null
}

// Get user's generated gift cards
export const getMyGiftCards = () => {
	return cloudRequest<{}, GiftCard[]>({
		url: '/user/gift-cards',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Get transaction history (recharge records)
export const getTransactionHistory = (limit: number = 50, skip: number = 0) => {
	interface Transaction {
		id: string
		type: number
		type_label: string
		description: string
		amount: number
		status: string
		created_at: number
		paid_at: number
	}
	interface Res {
		code: number
		data: Transaction[]
		total: number
	}
	return cloudRequest<{}, Res>({
		url: `/order/transactions?limit=${limit}&skip=${skip}`,
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// =====================================================
// Login Records API
// =====================================================

export interface LoginRecord {
	ts: number
	ip: string
	user_agent: string
	fingerprint: string
}

// Get recent login records
export const getLoginRecords = () => {
	return cloudRequest<null, any>({
		url: '/user/login-records',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// =====================================================
// Two-Factor Authentication (2FA) API
// =====================================================

// Get 2FA status
export const get2FAStatus = () => {
	interface Data {
		enabled: boolean
		hasBackupCodes: boolean
	}
	return cloudRequest<{}, Data>({
		url: '/user/2fa/status',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Setup 2FA (generate secret and QR code)
export const setup2FA = () => {
	interface Data {
		secret: string
		qrCode: string
		otpauth: string
	}
	return cloudRequest<{}, Data>({
		url: '/user/2fa/setup',
		method: 'POST',
		interceptors: commonInterceptors
	})
}

// Enable 2FA with verification code
export const enable2FA = (code: string) => {
	interface Data {
		backupCodes: string[]
	}
	return cloudRequest<{ code: string }, Data>({
		url: '/user/2fa/enable',
		method: 'POST',
		data: { code },
		interceptors: commonInterceptors
	})
}

// Disable 2FA
export const disable2FA = (password: string, code: string) => {
	return cloudRequest<{ password: string; code: string }, any>({
		url: '/user/2fa/disable',
		method: 'POST',
		data: { password, code },
		interceptors: commonInterceptors
	})
}

export interface AuditLogEntry {
	_id: string
	user_id: string
	user_email: string
	action: string
	resource_type: string
	resource_id?: string
	resource_name?: string
	details?: any
	ip_address?: string
	user_agent?: string
	created_at: string
}

export interface AuditLogsData {
	logs: AuditLogEntry[]
	total: number
	limit: number
	skip: number
}

export const getAuditLogs = (params: {
	limit?: number
	skip?: number
	action?: string
	start_date?: string
	end_date?: string
	search?: string
}) => {
	return cloudRequest<typeof params, AuditLogsData>({
		url: '/audit/logs',
		method: 'GET',
		data: params,
		interceptors: commonInterceptors
	})
}

export type AuditAction = { value: string; label: string }

export const getAuditActions = () => {
	return cloudRequest<any, AuditAction[]>({
		url: '/audit/actions',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// ============================================
// Admin User Management APIs
// ============================================

export interface AdminUser {
	_id: string
	email: string
	role: 'user' | 'admin'
	emailVerification?: { verified?: boolean }
	balance: number
	total_recharge: number
	twoFactor?: { enabled?: boolean }
	create_at: string
	last_login_at: string | null
}

export interface AdminUserStats {
	totalUsers: number
	adminUsers: number
	totalBalance: number
	totalRecharge: number
	activeUsers: number
	twoFAUsers: number
}

export interface AdminUserListResponse {
	users: AdminUser[]
	pagination: {
		page: number
		pageSize: number
		total: number
		totalPages: number
	}
}

export const getAdminUserStats = () => {
	return cloudRequest<any, AdminUserStats>({
		url: '/admin/users/stats',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

export const getAdminUsers = (params: {
	page?: number
	pageSize?: number
	search?: string
	sortField?: string
	sortOrder?: 'asc' | 'desc'
}) => {
	return cloudRequest<any, AdminUserListResponse>({
		url: '/admin/users',
		method: 'GET',
		data: params,
		interceptors: commonInterceptors
	})
}

// ============================================
// Fine-tuning Dataset APIs
// ============================================

export interface Dataset {
	id: string
	name: string
	description: string
	type: 'llm' | 'vision'
	format: string
	llm_format?: string
	vision_task?: string
	size_bytes: number
	row_count: number
	token_count?: number
	status: 'uploading' | 'processing' | 'ready' | 'error' | 'deleted'
	validated: boolean
	validation_status: string
	columns: Array<{ name: string; type: string; sample?: any }>
	created_at: number
	updated_at: number
}

export interface DatasetListResponse {
	datasets: Dataset[]
	total: number
}

export interface DatasetDetail extends Dataset {
	storage_path: string
	sample_rows: any[]
	validation_errors: string[]
	validation_warnings: string[]
	field_mapping?: {
		instruction_field?: string
		input_field?: string
		output_field?: string
		system_field?: string
		conversations_field?: string
	}
}

// List datasets
export const getDatasets = (params?: { type?: string; status?: string; limit?: number; offset?: number }) => {
	return cloudRequest<any, DatasetListResponse>({
		url: '/datasets',
		method: 'GET',
		data: params,
		interceptors: commonInterceptors
	})
}

// Get dataset details
export const getDataset = (id: string) => {
	return cloudRequest<any, DatasetDetail>({
		url: `/datasets/${id}`,
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Delete dataset
export const deleteDataset = (id: string) => {
	return cloudRequest<any, { msg: string }>({
		url: `/datasets/${id}`,
		method: 'DELETE',
		interceptors: commonInterceptors
	})
}

// Re-validate dataset
export const validateDataset = (id: string) => {
	return cloudRequest<any, { msg: string }>({
		url: `/datasets/${id}/validate`,
		method: 'POST',
		interceptors: commonInterceptors
	})
}

// Preview dataset
export const previewDataset = (id: string, rows?: number) => {
	return cloudRequest<any, { rows: any[]; columns: any[]; total_rows: number }>({
		url: `/datasets/${id}/preview`,
		method: 'GET',
		data: { rows },
		interceptors: commonInterceptors
	})
}

// ============================================
// Fine-tuning Training Job APIs
// ============================================

export interface TrainingJob {
	id: string
	name: string
	type: 'llm' | 'vision'
	status: 'queued' | 'pending' | 'running' | 'uploading' | 'compressing' | 'completed' | 'failed' | 'cancelled' | 'cancel_requested' | 'error' | 'paused'
	base_model: string
	training_method: string
	training_type?: string
	progress_percent: number
	current_epoch: number
	current_step?: number
	total_epochs: number
	metrics: {
		train_loss?: number
		eval_loss?: number
		train_accuracy?: number
		eval_accuracy?: number
		learning_rate?: number
		gpu_utilization?: number
		gpu_memory_used?: number
		samples_per_second?: number
	}
	estimated_cost: number
	actual_cost: number
	together_job_id?: string
	together_status?: string
	created_at: number
	started_at?: number
	completed_at?: number
	error_message?: string
}

export interface TrainingJobDetail extends TrainingJob {
	model_source?: string
	from_hf_model?: string
	hf_model_revision?: string
	hf_output_repo_name?: string
	total_steps?: number
	resumed_from_checkpoint?: string
	resume_count?: number
	hyperparameters: {
		learning_rate: number
		batch_size?: number
		epochs: number
		gradient_accumulation_steps?: number
		max_steps?: number
		warmup_steps?: number
		max_seq_length?: number
		from_checkpoint?: string
		lora_r?: number
		lora_alpha?: number
		lora_dropout?: number
		lora_trainable_modules?: string | string[]
		train_on_inputs?: string | boolean
		lr_scheduler?: string
		warmup_ratio?: number
		num_cycles?: number
		max_grad_norm?: number
		weight_decay?: number
		n_checkpoints?: number
		n_evals?: number
		suffix?: string
	}
	dataset_id: string
	validation_dataset_id?: string
	together_job_id?: string
	together_file_id?: string
	together_model_name?: string
	together_status?: string
	checkpoint_ids: string[]
	metrics_history: Array<{
		step: number
		epoch: number
		train_loss: number
		eval_loss?: number
		timestamp: number
	}>
	error_message?: string
}

export interface TrainingJobListResponse {
	jobs: TrainingJob[]
	total: number
}

export interface BaseModel {
	id: string
	name: string
	params: string
	organization: string
	supports_lora?: boolean
	supports_full?: boolean
	// Legacy fields (may not be present for upstream/managed models)
	vram_required?: number
	context_length?: number        // max SFT fine-tuning context (tokens)
	context_length_dpo?: number    // max DPO fine-tuning context (tokens)
	training_methods?: string[]
	task?: string
}

export interface ModelFilters {
	organizations: string[]
	sizeRanges: Array<{
		label: string
		value: string
		min: number
		max: number | null
	}>
}

// List available base models with optional filters
export const getBaseModels = (params?: {
	type?: 'llm' | 'vision'
	organization?: string
	min_size?: number
	max_size?: number
}) => {
	return cloudRequest<any, BaseModel[] | { llm: BaseModel[]; vision: BaseModel[] }>({
		url: '/training/models',
		method: 'GET',
		data: params,
		interceptors: commonInterceptors
	})
}

// Get available filter options for models
export const getModelFilters = () => {
	return cloudRequest<any, ModelFilters>({
		url: '/training/models/filters',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

export interface FinetuneTierPrices {
	small: number
	medium: number
	large: number
}

export interface FinetuneSpecializedPricingEntry {
	model: string
	sftLora: number
	dpoLora: number
	minimumCost: number
}

export interface FinetunePricingResponse {
	standardTiers: {
		sft_lora: FinetuneTierPrices
		sft_full: FinetuneTierPrices
		dpo_lora: FinetuneTierPrices
		dpo_full: FinetuneTierPrices
	}
	specializedModels: FinetuneSpecializedPricingEntry[]
}

export const getTrainingPricing = () => {
	return cloudRequest<any, FinetunePricingResponse>({
		url: '/training/pricing-public',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Create training job (upstream)
export const createTrainingJob = (data: {
	name: string
	base_model: string
	training_method?: string
	training_type?: string
	dataset_id: string
	validation_dataset_id?: string
	hyperparameters?: Record<string, any>
	suffix?: string
	model_source?: 'base' | 'huggingface'
	from_hf_model?: string
	hf_model_revision?: string
	hf_api_token?: string
}) => {
	return cloudRequest<any, { id: string; name: string; status: string; estimated_cost: number }>({
		url: '/training/jobs',
		method: 'POST',
		data,
		interceptors: commonInterceptors
	})
}

// Estimate training cost
export interface TrainingCostEstimate {
	estimated_cost: number
	final_cost: number
	price_per_million_tokens: number
	total_tokens: number
	training_tokens: number
	validation_tokens: number
	epochs: number
	n_evals: number
	model_size_b: number
	model_params: string
	size_tier: string
	minimum_cost: number
	is_specialized: boolean
}

export const estimateTrainingCost = (data: {
	base_model: string
	training_method: string
	training_type?: string
	dataset_id?: string
	validation_dataset_id?: string
	epochs?: number
	n_evals?: number
}) => {
	return cloudRequest<any, TrainingCostEstimate>({
		url: '/training/estimate',
		method: 'POST',
		data,
		interceptors: commonInterceptors
	})
}

// List training jobs
export const getTrainingJobs = (params?: { type?: string; status?: string; limit?: number; offset?: number }) => {
	return cloudRequest<any, TrainingJobListResponse>({
		url: '/training/jobs',
		method: 'GET',
		data: params,
		interceptors: commonInterceptors
	})
}

// Get training job details
export const getTrainingJob = (id: string) => {
	return cloudRequest<any, TrainingJobDetail & { dataset?: { id: string; name: string; row_count: number }; validation_dataset?: { id: string; name: string; row_count: number }; checkpoints: any[] }>({
		url: `/training/jobs/${id}`,
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Stop training job
export const stopTrainingJob = (id: string) => {
	return cloudRequest<any, { id: string; status: string; message: string }>({
		url: `/training/jobs/${id}/stop`,
		method: 'POST',
		interceptors: commonInterceptors
	})
}

// Cancel training job
export const cancelTrainingJob = (id: string) => {
	return cloudRequest<any, { id: string; status: string; message: string }>({
		url: `/training/jobs/${id}/cancel`,
		method: 'POST',
		interceptors: commonInterceptors
	})
}

// Delete training job
export const deleteTrainingJob = (id: string) => {
	return cloudRequest<any, { msg: string }>({
		url: `/training/jobs/${id}`,
		method: 'DELETE',
		interceptors: commonInterceptors
	})
}

// Get upstream events for a training job
export const getTrainingEvents = (id: string) => {
	return cloudRequest<any, { events: any[]; job_status: string; together_job_id?: string }>({
		url: `/training/jobs/${id}/events`,
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Download a checkpoint (triggers browser file download)
export const downloadCheckpoint = (jobId: string, checkpointId: string, type: 'merged' | 'adapter' = 'merged') => {
	const url = `${apiBaseUrl}/training/jobs/${jobId}/checkpoints/${checkpointId}/download?type=${type}`
	window.open(url, '_blank')
}

// Download the final checkpoint by type directly (no checkpoint ID needed)
export const downloadJobCheckpoint = (jobId: string, type: 'merged' | 'adapter' = 'merged') => {
	const url = `${apiBaseUrl}/training/jobs/${jobId}/download?type=${type}`
	window.open(url, '_blank')
}

// ============================================
// Fine-tuned Models APIs
// ============================================

export interface FinetunedModel {
	id: string
	job_id?: string
	name: string
	description: string
	base_model: string
	base_model_type: 'llm' | 'vision'
	num_parameters?: string
	context_length?: number
	training_method: string
	training_summary: {
		total_epochs: number
		total_steps: number
		final_train_loss: number
		final_eval_loss: number
		training_time_seconds: number
		total_cost: number
	}
	size_bytes: number
	storage_type?: 'local' | 's3' | 'huggingface' | 'together'
	together_model_name?: string
	deployed: boolean
	deployment?: {
		endpoint_id?: string
		hardware?: string
		status?: string  // Together.ai states: PENDING, STARTING, STARTED, STOPPING, STOPPED, ERROR
		deployed_at?: number
		cost_cents_per_min?: number
		inactive_timeout?: number | null
		endpoint_url?: string
		// Legacy fields
		pod_id?: string
		cost_per_hour?: number
	}
	created_at: number
}

// Hardware configuration for dedicated endpoints
export interface HardwareConfig {
	id: string
	name?: string
	gpu_type?: string
	gpu_count?: number
	gpu_memory?: string
	interconnect?: string
	cents_per_minute?: number
	pricing?: { cents_per_minute?: number }
	available?: boolean
}

export interface FinetunedModelListResponse {
	models: FinetunedModel[]
	total: number
}

// List fine-tuned models
export const getFinetunedModels = (params?: { type?: string; limit?: number; offset?: number }) => {
	return cloudRequest<any, FinetunedModelListResponse>({
		url: '/training/finetuned-models',
		method: 'GET',
		data: params,
		interceptors: commonInterceptors
	})
}

// Get fine-tuned model details
export const getFinetunedModel = (id: string) => {
	return cloudRequest<any, FinetunedModel>({
		url: `/training/finetuned-models/${id}`,
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// ============================================
// Model Deployment APIs (Dedicated Endpoints)
// ============================================

// Deploy a fine-tuned model as a dedicated endpoint
export const deployModel = (modelId: string, params: {
	hardware: string;
	inactive_timeout?: number | null;
	display_name?: string;
	availability_zone?: string;
	min_replicas?: number;
	max_replicas?: number;
	disable_speculative_decoding?: boolean;
}) => {
	return cloudRequest<any, { modelId: string; endpoint_id: string; status: string; hardware: string; cost_cents_per_min: number; message: string }>({
		url: `/deployment/deploy/${modelId}`,
		method: 'POST',
		data: params,
		interceptors: commonInterceptors
	})
}

// Undeploy a model (delete dedicated endpoint)
export const undeployModel = (modelId: string) => {
	return cloudRequest<any, { modelId: string; status: string; message: string }>({
		url: `/deployment/undeploy/${modelId}`,
		method: 'POST',
		interceptors: commonInterceptors
	})
}

// Get deployment status from Together.ai
export const getDeploymentStatus = (modelId: string) => {
	return cloudRequest<any, {
		deployed: boolean
		endpoint_id?: string
		status: string
		hardware?: string
		cost_cents_per_min?: number
		deployed_at?: number
		inactive_timeout?: number | null
		endpoint_url?: string
	}>({
		url: `/deployment/status/${modelId}`,
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// List all deployed endpoints for the current user
export const getEndpoints = () => {
	return cloudRequest<any, {
		model_id: string
		name: string
		together_model_name: string
		endpoint_id: string
		status: string
		hardware: string
		cost_cents_per_min: number
		deployed_at: number
		deployed: boolean
	}[]>({
		url: '/deployment/endpoints',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Start a stopped dedicated endpoint
export const startEndpoint = (modelId: string) => {
	return cloudRequest<any, { modelId: string; endpoint_id: string; status: string; message: string }>({
		url: `/deployment/start/${modelId}`,
		method: 'POST',
		interceptors: commonInterceptors
	})
}

// Stop a running dedicated endpoint
export const stopEndpoint = (modelId: string) => {
	return cloudRequest<any, { modelId: string; endpoint_id: string; status: string; message: string }>({
		url: `/deployment/stop/${modelId}`,
		method: 'POST',
		interceptors: commonInterceptors
	})
}

// List available GPU hardware configurations
export const getDeploymentGpus = (model?: string) => {
	return cloudRequest<any, HardwareConfig[]>({
		url: '/deployment/gpus',
		method: 'GET',
		data: model ? { model } : undefined,
		interceptors: commonInterceptors
	})
}

// ============================================
// Billing APIs (Fine-tuning & Endpoints)
// ============================================

export const getTrainingBilling = () => {
	return cloudRequest<any, {
		id: string
		base_model: string
		training_method: string
		status: string
		estimated_cost: number
		actual_cost: number
		billed_amount: number
		billed_at: number
		created_at: number
		completed_at: number
	}[]>({
		url: '/training/billing',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

export const getEndpointBilling = () => {
	return cloudRequest<any, {
		model_id: string
		name: string
		endpoint_id: string
		hardware: string
		status: string
		cost_cents_per_min: number
		total_running_seconds: number
		total_cost: number
		last_billed_at: number
		deployed_at: number
	}[]>({
		url: '/deployment/billing',
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// ============================================
// Support Ticket APIs
// ============================================

export interface TicketMessage {
	id: string
	sender_id: string
	sender_role: 'user' | 'admin'
	content: string
	images?: string[]
	created_at: number
}

export interface Ticket {
	id: string
	user_id: string
	title: string
	description: string
	images?: string[]
	category: 'billing' | 'technical' | 'account' | 'other'
	priority: 'low' | 'medium' | 'high' | 'urgent'
	status: 'open' | 'in_progress' | 'resolved' | 'closed'
	messages: TicketMessage[]
	message_count?: number
	created_at: number
	updated_at: number
	resolved_at: number | null
}

export interface TicketListResponse {
	tickets: Ticket[]
	total: number
}

export interface CreateTicketData {
	title: string
	description: string
	images?: string[]
	category?: 'billing' | 'technical' | 'account' | 'other'
	priority?: 'low' | 'medium' | 'high' | 'urgent'
}

// Create a new support ticket
export const createTicket = (data: CreateTicketData) => {
	return cloudRequest<CreateTicketData, { id: string; title: string; status: string; created_at: number }>({
		url: '/tickets',
		method: 'POST',
		data,
		interceptors: commonInterceptors
	})
}

// List user's tickets
export const getTickets = (params?: { status?: string; category?: string; priority?: string; limit?: number; offset?: number }) => {
	return cloudRequest<any, TicketListResponse>({
		url: '/tickets',
		method: 'GET',
		data: params,
		interceptors: commonInterceptors
	})
}

// Get ticket details
export const getTicketById = (id: string) => {
	return cloudRequest<any, Ticket>({
		url: `/tickets/${id}`,
		method: 'GET',
		interceptors: commonInterceptors
	})
}

// Add message to ticket
export const addTicketMessage = (id: string, content: string, images?: string[]) => {
	return cloudRequest<{ content: string; images?: string[] }, TicketMessage>({
		url: `/tickets/${id}/messages`,
		method: 'POST',
		data: { content, images },
		interceptors: commonInterceptors
	})
}

// Close ticket
export const closeTicket = (id: string) => {
	return cloudRequest<any, { msg: string }>({
		url: `/tickets/${id}/close`,
		method: 'PUT',
		interceptors: commonInterceptors
	})
}

// Upload ticket image
export const uploadTicketImage = (file: File) => {
	const formData = new FormData()
	formData.append('image', file)

	return fetch(`${apiBaseUrl}/tickets/images`, {
		method: 'POST',
		credentials: 'include',
		body: formData
	}).then(res => res.json())
}

// =====================================================
// Knowledge Distillation - Prompts API
// =====================================================

export const kdGetPrompts = (params?: any) => {
	return cloudRequest<any, any>({ url: '/kd/prompts', method: 'GET', data: params, interceptors: commonInterceptors })
}
export const kdGetPromptById = (id: string) => {
	return cloudRequest<any, any>({ url: `/kd/prompts/${id}`, method: 'GET', interceptors: commonInterceptors })
}
export const kdCreatePrompt = (data: any) => {
	return cloudRequest<any, any>({ url: '/kd/prompts', method: 'POST', data, interceptors: commonInterceptors })
}
export const kdCreateBulkPrompts = (prompts: any[]) => {
	return cloudRequest<any, any>({ url: '/kd/prompts/bulk', method: 'POST', data: { prompts }, interceptors: commonInterceptors })
}
export const kdUpdatePrompt = (id: string, data: any) => {
	return cloudRequest<any, any>({ url: `/kd/prompts/${id}`, method: 'PUT', data, interceptors: commonInterceptors })
}
export const kdDeletePrompt = (id: string) => {
	return cloudRequest<any, any>({ url: `/kd/prompts/${id}`, method: 'DELETE', interceptors: commonInterceptors })
}
export const kdDeleteAllPrompts = () => {
	return cloudRequest<any, any>({ url: '/kd/prompts', method: 'DELETE', interceptors: commonInterceptors })
}
export const kdSearchPrompts = (query: any) => {
	return cloudRequest<any, any>({ url: '/kd/prompts/search', method: 'POST', data: query, interceptors: commonInterceptors })
}
export const kdGetPromptStats = () => {
	return cloudRequest<any, any>({ url: '/kd/prompts/stats/overview', method: 'GET', interceptors: commonInterceptors })
}

// =====================================================
// Knowledge Distillation - Export API
// =====================================================

export const kdGetExportStats = (params?: { category?: string; source?: string; tags?: string[]; importJobId?: string }) => {
	return cloudRequest<any, any>({ url: '/kd/prompts/export/stats', method: 'GET', data: params, interceptors: commonInterceptors })
}
export const kdExportForFinetune = (options: {
	format?: 'openai' | 'alpaca' | 'sharegpt' | 'chatml';
	category?: string; source?: string; tags?: string[]; importJobId?: string;
	limit?: number; trainSplit?: number; includeMetadata?: boolean; systemPrompt?: string;
}) => {
	return cloudRequest<any, any>({ url: '/kd/prompts/export/finetune', method: 'POST', data: options, interceptors: commonInterceptors })
}
export const kdGetExportDownloadUrl = (params: {
	format?: string; split?: 'train' | 'validation' | 'all';
	category?: string; source?: string; tags?: string[]; importJobId?: string;
	limit?: number; trainSplit?: number; systemPrompt?: string; includeMetadata?: boolean;
}) => {
	const searchParams = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value === undefined) return;
		if (Array.isArray(value)) {
			value.forEach((v) => searchParams.append(key, String(v)));
		} else {
			searchParams.append(key, String(value));
		}
	});
	return `${apiBaseUrl}/kd/prompts/export/download?${searchParams.toString()}`;
}

// =====================================================
// Knowledge Distillation - HuggingFace API
// =====================================================

export const kdHuggingfaceSearch = (query: string, options?: { limit?: number; filter?: string; sort?: string }) => {
	return cloudRequest<any, any>({ url: '/kd/prompts/huggingface/search', method: 'GET', data: { q: query, ...options }, interceptors: commonInterceptors })
}
export const kdHuggingfaceGetSuggested = (options?: { category?: string; minDownloads?: number; minLikes?: number }) => {
	return cloudRequest<any, any>({ url: '/kd/prompts/huggingface/suggested', method: 'GET', data: options || {}, interceptors: commonInterceptors })
}
export const kdHuggingfaceSyncDatasets = (category?: string) => {
	return cloudRequest<any, any>({ url: '/kd/prompts/huggingface/sync', method: 'POST', data: { category: category || 'all' }, interceptors: commonInterceptors })
}
export const kdHuggingfaceGetSyncStatus = () => {
	return cloudRequest<any, any>({ url: '/kd/prompts/huggingface/sync/status', method: 'GET', interceptors: commonInterceptors })
}
export const kdHuggingfaceGetSyncProgress = () => {
	return cloudRequest<any, any>({ url: '/kd/prompts/huggingface/sync/progress', method: 'GET', interceptors: commonInterceptors })
}
export const kdHuggingfaceGetDatasetInfo = (datasetId: string) => {
	return cloudRequest<any, any>({ url: `/kd/prompts/huggingface/dataset/${datasetId}`, method: 'GET', interceptors: commonInterceptors })
}
export const kdHuggingfaceGetDatasetConfigs = (datasetId: string) => {
	return cloudRequest<any, any>({ url: `/kd/prompts/huggingface/dataset/${datasetId}/configs`, method: 'GET', interceptors: commonInterceptors })
}
export const kdHuggingfacePreviewDataset = (datasetId: string, params?: { config?: string; split?: string; limit?: number }) => {
	return cloudRequest<any, any>({ url: `/kd/prompts/huggingface/dataset/${datasetId}/preview`, method: 'GET', data: params, interceptors: commonInterceptors })
}
export const kdHuggingfaceImportAllSuggested = (options?: {
	limitPerDataset?: number; category?: string; generateResponses?: boolean;
	providers?: Array<{ type: string; customIndex?: number; model: string }>;
	datasetIds?: string[];
}) => {
	return cloudRequest<any, any>({ url: '/kd/prompts/huggingface/import-all-suggested', method: 'POST', data: options || {}, interceptors: commonInterceptors })
}

// =====================================================
// Knowledge Distillation - Generation API
// =====================================================

export const kdGetJobs = (params?: any) => {
	return cloudRequest<any, any>({ url: '/kd/prompts/jobs', method: 'GET', data: params, interceptors: commonInterceptors })
}
export const kdGetJobById = (id: string) => {
	return cloudRequest<any, any>({ url: `/kd/prompts/jobs/${id}`, method: 'GET', interceptors: commonInterceptors })
}
export const kdGetJobPrompts = (jobId: string, params?: { limit?: number; skip?: number }) => {
	return cloudRequest<any, any>({ url: `/kd/prompts/jobs/${jobId}/prompts`, method: 'GET', data: params, interceptors: commonInterceptors })
}
export const kdGenerateSynthetic = (data: {
	providers: Array<{ type: string; customIndex?: number; model: string }>;
	category: string; subcategory?: string; count: number;
	generateResponses?: boolean; responseModel?: string;
	temperature?: number; responseTemperature?: number;
	systemPrompt?: string; topics?: string[];
}) => {
	return cloudRequest<any, any>({ url: '/kd/prompts/generate/synthetic', method: 'POST', data, interceptors: commonInterceptors })
}
export const kdScrapeGithub = (data: {
	repoUrl: string; language?: string;
	providers: Array<{ type: string; customIndex?: number; model: string }>;
	generateResponses?: boolean;
	stage1Provider?: { enabled: boolean; model: string };
}) => {
	return cloudRequest<any, any>({ url: '/kd/prompts/generate/github', method: 'POST', data, interceptors: commonInterceptors })
}
export const kdAnalyzeGithubProjects = (params: {
	owner: string; ownerType?: 'user' | 'org'; maxRepos?: number;
	includeForks?: boolean; includeArchived?: boolean;
	minStars?: number; nameIncludes?: string;
}) => {
	return cloudRequest<any, any>({ url: '/kd/prompts/generate/github/projects', method: 'GET', data: params, interceptors: commonInterceptors })
}
export const kdScrapeGithubBulk = (data: {
	owner?: string; ownerType?: 'user' | 'org'; selectedRepos?: string[];
	maxRepos?: number; includeForks?: boolean; includeArchived?: boolean;
	minStars?: number; nameIncludes?: string; language?: string;
	providers: Array<{ type: string; customIndex?: number; model: string }>;
	generateResponses?: boolean;
	stage1Provider?: { enabled: boolean; model: string };
}) => {
	return cloudRequest<any, any>({ url: '/kd/prompts/generate/github/bulk', method: 'POST', data, interceptors: commonInterceptors })
}
export const kdResumeGithub = (jobId: string, data?: {
	providers?: Array<{ type: string; customIndex?: number; model: string }>;
	generateResponses?: boolean;
	stage1Provider?: { enabled: boolean; model: string };
}) => {
	return cloudRequest<any, any>({ url: `/kd/prompts/generate/github/resume/${jobId}`, method: 'POST', data: data || {}, interceptors: commonInterceptors })
}
export const kdPauseGithub = (jobId: string) => {
	return cloudRequest<any, any>({ url: `/kd/prompts/generate/github/pause/${jobId}`, method: 'POST', interceptors: commonInterceptors })
}
export const kdPauseSynthetic = (jobId: string) => {
	return cloudRequest<any, any>({ url: `/kd/prompts/generate/synthetic/pause/${jobId}`, method: 'POST', interceptors: commonInterceptors })
}
export const kdResumeSynthetic = (jobId: string) => {
	return cloudRequest<any, any>({ url: `/kd/prompts/generate/synthetic/resume/${jobId}`, method: 'POST', interceptors: commonInterceptors })
}
export const kdReanalyzeSynthetic = (jobId: string) => {
	return cloudRequest<any, any>({ url: `/kd/prompts/generate/synthetic/reanalyze/${jobId}`, method: 'POST', interceptors: commonInterceptors })
}
export const kdPauseHuggingface = (jobId: string) => {
	return cloudRequest<any, any>({ url: `/kd/prompts/huggingface/pause/${jobId}`, method: 'POST', interceptors: commonInterceptors })
}
export const kdResumeHuggingface = (jobId: string) => {
	return cloudRequest<any, any>({ url: `/kd/prompts/huggingface/resume/${jobId}`, method: 'POST', interceptors: commonInterceptors })
}
export const kdReanalyzeHuggingface = (jobId: string) => {
	return cloudRequest<any, any>({ url: `/kd/prompts/huggingface/reanalyze/${jobId}`, method: 'POST', interceptors: commonInterceptors })
}
export const kdReanalyzeGithub = (jobId: string, data?: {
	providers?: Array<{ type: string; customIndex?: number; model: string }>;
	generateResponses?: boolean;
	stage1Provider?: { enabled: boolean; model: string };
}) => {
	return cloudRequest<any, any>({ url: `/kd/prompts/generate/github/reanalyze/${jobId}`, method: 'POST', data: data || {}, interceptors: commonInterceptors })
}
export const kdDeleteJob = (jobId: string) => {
	return cloudRequest<any, any>({ url: `/kd/prompts/jobs/${jobId}`, method: 'DELETE', interceptors: commonInterceptors })
}
export const kdGetResumableGithubJobs = () => {
	return cloudRequest<any, any>({ url: '/kd/prompts/generate/github/resumable', method: 'GET', interceptors: commonInterceptors })
}

// =====================================================
// Knowledge Distillation - Analytics API
// =====================================================

export const kdGetAnalyticsDashboard = () => {
	return cloudRequest<any, any>({ url: '/kd/analytics/dashboard', method: 'GET', interceptors: commonInterceptors })
}
export const kdGetCostByProvider = () => {
	return cloudRequest<any, any>({ url: '/kd/analytics/costs/by-provider', method: 'GET', interceptors: commonInterceptors })
}
export const kdGetCostByModel = () => {
	return cloudRequest<any, any>({ url: '/kd/analytics/costs/by-model', method: 'GET', interceptors: commonInterceptors })
}
export const kdGetCostOverTime = (days = 30) => {
	return cloudRequest<any, any>({ url: `/kd/analytics/costs/over-time`, method: 'GET', data: { days }, interceptors: commonInterceptors })
}
export const kdGetTokenStats = () => {
	return cloudRequest<any, any>({ url: '/kd/analytics/tokens', method: 'GET', interceptors: commonInterceptors })
}
export const kdGetPerformance = () => {
	return cloudRequest<any, any>({ url: '/kd/analytics/performance', method: 'GET', interceptors: commonInterceptors })
}
export const kdGetJobCompletionRate = (days = 30) => {
	return cloudRequest<any, any>({ url: `/kd/analytics/jobs/completion-rate`, method: 'GET', data: { days }, interceptors: commonInterceptors })
}
export const kdGetMostUsedPrompts = (limit = 10) => {
	return cloudRequest<any, any>({ url: `/kd/analytics/prompts/most-used`, method: 'GET', data: { limit }, interceptors: commonInterceptors })
}

// =====================================================
// Knowledge Distillation - Settings API
// =====================================================

export const kdGetSettings = () => {
	return cloudRequest<any, any>({ url: '/kd/settings', method: 'GET', interceptors: commonInterceptors })
}
export const kdUpdateSettings = (updates: any) => {
	return cloudRequest<any, any>({ url: '/kd/settings', method: 'PUT', data: updates, interceptors: commonInterceptors })
}
export const kdUpdateCloudProvider = (provider: 'openrouter' | 'together' | 'gateway', updates: any) => {
	return cloudRequest<any, any>({ url: `/kd/settings/provider/${provider}`, method: 'PUT', data: updates, interceptors: commonInterceptors })
}
export const kdAddCustomProvider = (provider: any) => {
	return cloudRequest<any, any>({ url: '/kd/settings/provider/custom', method: 'POST', data: provider, interceptors: commonInterceptors })
}
export const kdUpdateCustomProvider = (index: number, updates: any) => {
	return cloudRequest<any, any>({ url: `/kd/settings/provider/custom/${index}`, method: 'PUT', data: updates, interceptors: commonInterceptors })
}
export const kdDeleteCustomProvider = (index: number) => {
	return cloudRequest<any, any>({ url: `/kd/settings/provider/custom/${index}`, method: 'DELETE', interceptors: commonInterceptors })
}
export const kdSetDefaultProvider = (provider: string, customIndex?: number) => {
	return cloudRequest<any, any>({ url: '/kd/settings/default-provider', method: 'PUT', data: { provider, customIndex }, interceptors: commonInterceptors })
}
export const kdTestConnection = (data: {
	provider: string; customIndex?: number; apiKey?: string; baseUrl?: string; model?: string;
}) => {
	return cloudRequest<any, any>({ url: '/kd/settings/test-connection', method: 'POST', data, interceptors: commonInterceptors })
}
export const kdGetProviderModels = (provider: string, customIndex?: number) => {
	return cloudRequest<any, any>({ url: `/kd/settings/models/${provider}`, method: 'GET', data: customIndex !== undefined ? { customIndex } : {}, interceptors: commonInterceptors })
}
export const kdGetOpenRouterUsage = () => {
	return cloudRequest<any, any>({ url: '/kd/settings/openrouter/usage', method: 'GET', interceptors: commonInterceptors })
}
export const kdFetchOpenRouterModels = (apiKey?: string, save: boolean = true) => {
	return cloudRequest<any, any>({ url: '/kd/settings/openrouter/fetch-models', method: 'POST', data: { apiKey, save }, interceptors: commonInterceptors })
}
export const kdFetchTogetherModels = (apiKey?: string, save: boolean = true) => {
	return cloudRequest<any, any>({ url: '/kd/settings/together/fetch-models', method: 'POST', data: { apiKey, save }, interceptors: commonInterceptors })
}
export const kdFetchGatewayModels = (apiKey?: string, save: boolean = true) => {
	return cloudRequest<any, any>({ url: '/kd/settings/gateway/fetch-models', method: 'POST', data: { apiKey, save }, interceptors: commonInterceptors })
}
export const kdGetTogetherUsage = () => {
	return cloudRequest<any, any>({ url: '/kd/settings/together/usage', method: 'GET', interceptors: commonInterceptors })
}
export const kdGetGatewayUsage = (params?: { mode?: 'provider' | 'auto' | 'app'; days?: number }) => {
	return cloudRequest<any, any>({ url: '/kd/settings/gateway/usage', method: 'GET', data: params, interceptors: commonInterceptors })
}
export const kdGetGatewayApiKeys = () => {
	return cloudRequest<any, any>({ url: '/kd/settings/gateway/api-keys', method: 'GET', interceptors: commonInterceptors })
}
export const kdGetGatewayApiKeyUsage = (keyId: string, params?: { days?: number }) => {
	return cloudRequest<any, any>({ url: `/kd/settings/gateway/api-keys/${keyId}/usage`, method: 'GET', data: params, interceptors: commonInterceptors })
}
export const kdGetModelUsageStats = () => {
	return cloudRequest<any, any>({ url: '/kd/settings/statistics/model-usage', method: 'GET', interceptors: commonInterceptors })
}
export const kdGetLlmUsageStats = (params?: { days?: number; provider?: string; include?: 'combined' | 'prompts' | 'jobs' }) => {
	return cloudRequest<any, any>({ url: '/kd/settings/statistics/llm-usage', method: 'GET', data: params, interceptors: commonInterceptors })
}

// =====================================================
// Knowledge Distillation - Finetune API
// =====================================================

export const kdFinetuneUploadFile = (fileContent: string, fileName: string) => {
	return cloudRequest<any, any>({ url: '/kd/finetune/upload', method: 'POST', data: { fileContent, fileName, purpose: 'fine-tune' }, interceptors: commonInterceptors })
}
export const kdFinetuneListFiles = () => {
	return cloudRequest<any, any>({ url: '/kd/finetune/files', method: 'GET', interceptors: commonInterceptors })
}
export const kdFinetuneExportAndUpload = (options: {
	format?: string; category?: string; source?: string; tags?: string[];
	importJobId?: string; limit?: number; trainSplit?: number;
	systemPrompt?: string; split?: 'train' | 'validation' | 'all'; fileName?: string;
}) => {
	return cloudRequest<any, any>({ url: '/kd/finetune/export-and-upload', method: 'POST', data: options, interceptors: commonInterceptors })
}
export const kdFinetuneCreateJob = (config: any) => {
	return cloudRequest<any, any>({ url: '/kd/finetune/jobs', method: 'POST', data: config, interceptors: commonInterceptors })
}
export const kdFinetuneListJobs = () => {
	return cloudRequest<any, any>({ url: '/kd/finetune/jobs', method: 'GET', interceptors: commonInterceptors })
}
export const kdFinetuneGetJob = (id: string) => {
	return cloudRequest<any, any>({ url: `/kd/finetune/jobs/${id}`, method: 'GET', interceptors: commonInterceptors })
}
export const kdFinetuneGetJobEvents = (id: string) => {
	return cloudRequest<any, any>({ url: `/kd/finetune/jobs/${id}/events`, method: 'GET', interceptors: commonInterceptors })
}
export const kdFinetuneCancelJob = (id: string) => {
	return cloudRequest<any, any>({ url: `/kd/finetune/jobs/${id}/cancel`, method: 'POST', interceptors: commonInterceptors })
}
export const kdFinetuneGetCheckpoints = (id: string) => {
	return cloudRequest<any, any>({ url: `/kd/finetune/jobs/${id}/checkpoints`, method: 'GET', interceptors: commonInterceptors })
}
export const kdFinetuneGetModels = () => {
	return cloudRequest<any, any>({ url: '/kd/finetune/models', method: 'GET', interceptors: commonInterceptors })
}

// =====================================================
// Knowledge Distillation - Disassembly API
// =====================================================

export const kdDisassemblyUpload = (file: File, onProgress?: (progress: number) => void) => {
	const formData = new FormData()
	formData.append('binary', file, file.name)
	return fetch(`${apiBaseUrl}/kd/disassembly/upload`, {
		method: 'POST',
		credentials: 'include',
		body: formData
	}).then(res => res.json())
}
export const kdDisassemblyGetAnalysis = (id: string) => {
	return cloudRequest<any, any>({ url: `/kd/disassembly/${id}`, method: 'GET', interceptors: commonInterceptors })
}
export const kdDisassemblyGetFunction = (binaryId: string, address: string) => {
	return cloudRequest<any, any>({ url: `/kd/disassembly/${binaryId}/function/${address}`, method: 'GET', interceptors: commonInterceptors })
}
export const kdDisassemblyGetTemplates = () => {
	return cloudRequest<any, any>({ url: '/kd/disassembly/templates/list', method: 'GET', interceptors: commonInterceptors })
}
export const kdDisassemblyGeneratePrompts = (binaryId: string, data: { functions: string[]; templates: string[] }) => {
	return cloudRequest<any, any>({ url: `/kd/disassembly/${binaryId}/generate-prompts`, method: 'POST', data, interceptors: commonInterceptors })
}
export const kdDisassemblyCloseAnalysis = (id: string) => {
	return cloudRequest<any, any>({ url: `/kd/disassembly/${id}`, method: 'DELETE', interceptors: commonInterceptors })
}
